import React, { useState } from "react";
import { Plus, Trash2, RefreshCw, ChevronDown, ChevronUp, Pencil, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";
import { formatTime12h, getEffectiveStartTime, calculateCallTime, getTodayString } from "../eventTime";
import { useTableControls, SearchBar, SortSelect, applyControls } from "../tableTools";

const LOCATION_FILTERS = ["Forum", "Studio 214", "ALL"];
const matchesLocation = (event, filter) => {
  if (filter === "ALL") return true;
  const loc = (event.location || "").toLowerCase();
  if (filter === "Forum") return loc.includes("forum");
  if (filter === "Studio 214") return loc.includes("214") || loc.includes("studio 214");
  return true;
};

const EVENT_SORT_FIELDS = [
  { value: "date", label: "Date" },
  { value: "name", label: "Event Name" },
  { value: "location", label: "Location" },
  { value: "start_time", label: "Show Time" },
];
const EVENT_SEARCH_FIELDS = [
  { value: "name", label: "Event Name" },
  { value: "location", label: "Location" },
  { value: "date", label: "Date" },
];
const eventGetValue = (e, field) => {
  switch (field) {
    case "date": return e.date;
    case "name": return e.name;
    case "location": return e.location;
    case "start_time": return e.start_time;
    default: return "";
  }
};

export const EventsTab = (props) => {
  const {
    events,
    dialogOpen,
    expandedRows,
    syncingMonday,
    editingEventId,
    editEventData,
    newEvent,
    setDialogOpen,
    setEditEventData,
    setNewEvent,
    handleSyncMonday,
    handleEditEvent,
    handleSaveEvent,
    handleCancelEdit,
    handleCreateEvent,
    handleDeleteEvent,
    handleUpdateEventStatus,
    toggleRowExpand,
    mondaySyncStatus,
  } = props;
  const lastSynced = mondaySyncStatus && mondaySyncStatus.last_synced_at
    ? new Date(mondaySyncStatus.last_synced_at).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
      })
    : null;
  const ctrl = useTableControls(null, "name");
  const [locationFilter, setLocationFilter] = useState("Forum");
  const displayEvents = applyControls(events, ctrl, eventGetValue).filter((e) => matchesLocation(e, locationFilter));
  return (
          <TabsContent value="events" data-testid="events-content">
            <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
              <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-crew-yellow">EVENTS</h2>
                  <p className="text-zinc-500 text-sm">{displayEvents.length} events ({locationFilter}) • Click row to expand details</p>
                  <p className="text-xs text-zinc-500 mt-1" data-testid="monday-last-synced">
                    {lastSynced
                      ? <>Last synced from Monday.com: <span className="text-zinc-300">{lastSynced}</span></>
                      : 'Not yet synced from Monday.com'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleSyncMonday}
                    disabled={syncingMonday}
                    variant="outline"
                    className="border-crew-yellow text-crew-yellow font-bold uppercase hover:bg-crew-yellow hover:text-black"
                    data-testid="sync-monday-button"
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${syncingMonday ? 'animate-spin' : ''}`} />
                    {syncingMonday ? 'Syncing…' : 'Sync from Monday.com'}
                  </Button>
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="bg-crew-yellow text-black font-bold uppercase hover:bg-white"
                      data-testid="create-event-button"
                    >
                      <Plus className="mr-2" /> Create Event
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-crew-zinc border-2 border-zinc-800 text-white max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="text-crew-yellow text-2xl font-bold">CREATE NEW EVENT</DialogTitle>
                      <DialogDescription className="text-zinc-400">
                        Add a new event to the Crew Call schedule.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateEvent} className="space-y-4 py-4">
                      <div>
                        <Label htmlFor="event-name" className="text-zinc-300 font-bold uppercase text-xs">Event Name</Label>
                        <Input
                          id="event-name"
                          value={newEvent.name}
                          onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                          required
                          className="bg-black border-2 border-zinc-800 focus:border-crew-yellow text-white"
                          data-testid="event-name-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="event-date" className="text-zinc-300 font-bold uppercase text-xs">Date</Label>
                          <Input
                            id="event-date"
                            type="date"
                            value={newEvent.date}
                            onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                            required
                            className="bg-black border-2 border-zinc-800 focus:border-crew-yellow text-white"
                            data-testid="event-date-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="event-time" className="text-zinc-300 font-bold uppercase text-xs">Crew Call Time</Label>
                          <Input
                            id="event-time"
                            type="time"
                            value={newEvent.call_time}
                            onChange={(e) => setNewEvent({ ...newEvent, call_time: e.target.value })}
                            required
                            className="bg-black border-2 border-zinc-800 focus:border-crew-yellow text-white"
                            data-testid="event-time-input"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="event-description" className="text-zinc-300 font-bold uppercase text-xs">Description (Optional)</Label>
                        <Textarea
                          id="event-description"
                          value={newEvent.description}
                          onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                          rows={3}
                          className="bg-black border-2 border-zinc-800 focus:border-crew-yellow text-white"
                          data-testid="event-description-input"
                        />
                      </div>
                      <DialogFooter>
                        <Button
                          type="submit"
                          className="bg-crew-yellow text-black font-bold hover:bg-white"
                          data-testid="submit-event-button"
                        >
                          Create Event
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
              </div>

              <div className="p-4 border-b border-zinc-800 flex flex-col md:flex-row md:items-center gap-3 bg-zinc-900/40">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase font-bold text-zinc-500">Location</span>
                  <Select value={locationFilter} onValueChange={setLocationFilter}>
                    <SelectTrigger className="bg-black border-zinc-700 text-white w-36 h-9" data-testid="event-location-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      {LOCATION_FILTERS.map((l) => (
                        <SelectItem key={l} value={l} className="text-white hover:bg-zinc-800">{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-xs uppercase font-bold text-zinc-500 md:ml-2">Sort &amp; Search</span>
                <SortSelect fields={EVENT_SORT_FIELDS} testid="event-sort" {...ctrl} />
                <SearchBar fields={EVENT_SEARCH_FIELDS} testid="event-search" {...ctrl} />
              </div>

              {displayEvents.length === 0 ? (
                <div className="p-12 text-center text-zinc-500" data-testid="no-events-message">
                  {events.length === 0 ? 'No events created yet. Click "Create Event" to get started.' : `No events match the current filter (${locationFilter}) or search.`}
                </div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {displayEvents.map((event) => {
                    const isExpanded = expandedRows[`event-${event.id}`];
                    const eventIsToday = event.date === getTodayString();
                    const eventIsPast = event.date < getTodayString();
                    const isCancelled = event.status === 'cancelled';
                    
                    return (
                      <div key={event.id} data-testid={`event-row-${event.id}`}>
                        {/* Main Row - Clickable */}
                        <div 
                          className={`p-4 hover:bg-zinc-900/50 transition-colors cursor-pointer flex items-center justify-between ${eventIsToday ? 'bg-crew-yellow/10 border-l-4 border-l-crew-yellow' : ''} ${eventIsPast ? 'opacity-60' : ''} ${isCancelled ? 'bg-red-900/10' : ''}`}
                          onClick={() => toggleRowExpand(`event-${event.id}`)}
                        >
                          <div className="flex items-center gap-4 flex-1">
                            {/* Expand Icon */}
                            <div className="text-zinc-500">
                              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </div>
                            
                            {/* Date */}
                            <div className="w-28">
                              <div className={`font-mono text-sm ${eventIsToday ? 'text-crew-yellow font-bold' : 'text-crew-yellow'} ${isCancelled ? 'line-through opacity-60' : ''}`}>
                                {event.date || '—'}
                              </div>
                              {eventIsToday && !isCancelled && <span className="text-xs text-crew-yellow font-bold">TODAY</span>}
                              {eventIsPast && !isCancelled && <span className="text-xs text-zinc-500">PAST</span>}
                              {isCancelled && <span className="text-xs text-red-500 font-bold">CANCELLED</span>}
                            </div>
                            
                            {/* Crew Call Time */}
                            <div className="w-24">
                              <span className={`font-mono text-crew-yellow font-bold ${isCancelled ? 'line-through opacity-60' : ''}`}>
                                {formatTime12h(calculateCallTime(event.start_time, event.call_time, event.description || event.notes))}
                              </span>
                            </div>
                            
                            {/* Event Name */}
                            <div className="flex-1">
                              <h3 className={`text-lg font-bold text-white ${isCancelled ? 'line-through opacity-60' : ''}`}>{event.name}</h3>
                              {event.location && (
                                <span className={`text-sm text-zinc-500 ${isCancelled ? 'line-through' : ''}`}>{event.location}</span>
                              )}
                              {(event.livestream_youtube || event.recording_required) && (
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {event.livestream_youtube && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-300">● LIVESTREAM (YouTube)</span>
                                  )}
                                  {event.recording_required && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">● RECORDING</span>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Cancel/Restore Toggle */}
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleUpdateEventStatus(event.id, isCancelled ? 'confirmed' : 'cancelled'); }}
                              variant="outline"
                              size="sm"
                              className={isCancelled ? 'border-green-500 text-green-500 hover:bg-green-500 hover:text-black' : 'border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black'}
                              title={isCancelled ? 'Restore Event' : 'Cancel Event'}
                            >
                              {isCancelled ? 'Restore' : 'Cancel'}
                            </Button>
                          </div>
                          
                          {/* Delete Button */}
                          <Button
                            onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }}
                            variant="ghost"
                            size="icon"
                            className="text-zinc-500 hover:text-red-500 ml-2"
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                        
                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="bg-zinc-900/50 border-t border-zinc-800 p-4">
                            {editingEventId === event.id ? (
                              /* Edit Mode */
                              <div>
                                <div className="flex items-center justify-between mb-4">
                                  <h4 className="text-sm font-bold text-crew-yellow uppercase">Edit Event Details</h4>
                                  <div className="flex gap-2">
                                    <Button
                                      onClick={() => handleSaveEvent(event.id)}
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-500 text-white"
                                    >
                                      <Save size={14} className="mr-1" /> Save
                                    </Button>
                                    <Button
                                      onClick={handleCancelEdit}
                                      size="sm"
                                      variant="outline"
                                      className="border-zinc-600 text-zinc-400"
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                  <div>
                                    <Label className="text-xs text-zinc-500 uppercase font-bold">Event Date</Label>
                                    <div className="text-white font-mono mt-1">{event.date || '—'}</div>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-crew-yellow uppercase font-bold">Crew Call Time *</Label>
                                    <Input
                                      type="time"
                                      value={editEventData.call_time || ''}
                                      onChange={(e) => setEditEventData({...editEventData, call_time: e.target.value})}
                                      className="bg-black border-crew-yellow text-white mt-1 h-9"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-zinc-500 uppercase font-bold">Start Time</Label>
                                    <Input
                                      type="time"
                                      value={editEventData.start_time || ''}
                                      onChange={(e) => setEditEventData({...editEventData, start_time: e.target.value})}
                                      className="bg-black border-zinc-700 text-white mt-1 h-9"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-zinc-500 uppercase font-bold">End Time</Label>
                                    <Input
                                      type="time"
                                      value={editEventData.end_time || ''}
                                      onChange={(e) => setEditEventData({...editEventData, end_time: e.target.value})}
                                      className="bg-black border-zinc-700 text-white mt-1 h-9"
                                    />
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <Label className="text-xs text-zinc-500 uppercase font-bold">Location</Label>
                                    <Input
                                      type="text"
                                      value={editEventData.location || ''}
                                      onChange={(e) => setEditEventData({...editEventData, location: e.target.value})}
                                      placeholder="e.g., Forum, Main Hall"
                                      className="bg-black border-zinc-700 text-white mt-1"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-zinc-500 uppercase font-bold">Source</Label>
                                    <div className="text-zinc-400 text-sm mt-2">{event.source || 'Manual Entry'}</div>
                                  </div>
                                </div>
                                
                                <div>
                                  <Label className="text-xs text-zinc-500 uppercase font-bold">Notes / Description</Label>
                                  <Textarea
                                    value={editEventData.notes || ''}
                                    onChange={(e) => setEditEventData({...editEventData, notes: e.target.value})}
                                    rows={3}
                                    className="bg-black border-zinc-700 text-white mt-1"
                                    placeholder="Add event notes..."
                                  />
                                </div>

                                <div className="mt-4">
                                  <Label className="text-xs text-zinc-500 uppercase font-bold">Website URL</Label>
                                  <Input
                                    type="url"
                                    data-testid="edit-event-website-url-input"
                                    value={editEventData.website_url || ''}
                                    onChange={(e) => setEditEventData({...editEventData, website_url: e.target.value})}
                                    placeholder="https://westportlibrary.org/events/..."
                                    className="bg-black border-zinc-700 text-white mt-1"
                                  />
                                  <p className="text-xs text-zinc-600 mt-1">Paste the official event page link. Crew members will see a "View Event" link.</p>
                                </div>
                                
                                <p className="text-xs text-zinc-500 mt-3">
                                  * Changes to Crew Call Time will update all screens and email communications.
                                </p>
                              </div>
                            ) : (
                              /* View Mode */
                              <div>
                                <div className="flex items-center justify-between mb-4">
                                  <span className="text-xs text-zinc-500">Event Details</span>
                                  <Button
                                    onClick={(e) => { e.stopPropagation(); handleEditEvent(event); }}
                                    size="sm"
                                    variant="outline"
                                    className="border-crew-yellow text-crew-yellow hover:bg-crew-yellow hover:text-black"
                                  >
                                    <Pencil size={14} className="mr-1" /> Edit
                                  </Button>
                                </div>
                                
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                  <div>
                                    <div className="text-xs text-zinc-500 uppercase font-bold mb-1">Event Date</div>
                                    <div className="text-white font-mono">{event.date || '—'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-crew-yellow uppercase font-bold mb-1">Crew Call Time</div>
                                    <div className="text-crew-yellow font-bold font-mono text-lg">
                                      {formatTime12h(calculateCallTime(event.start_time, event.call_time, event.description || event.notes))}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-zinc-500 uppercase font-bold mb-1">Show Start Time</div>
                                    <div className="text-white font-mono">
                                      {formatTime12h(getEffectiveStartTime(event.start_time, event.description || event.notes)) || '—'}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-zinc-500 uppercase font-bold mb-1">End Time</div>
                                    <div className="text-white font-mono">{formatTime12h(event.end_time) || '—'}</div>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <div className="text-xs text-zinc-500 uppercase font-bold mb-1">Location</div>
                                    <div className="text-white">{event.location || '—'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-zinc-500 uppercase font-bold mb-1">Source</div>
                                    <div className="text-zinc-400 text-sm">{event.source || 'Manual Entry'}</div>
                                  </div>
                                </div>

                                {event.website_url && (
                                  <div className="mb-4">
                                    <div className="text-xs text-zinc-500 uppercase font-bold mb-1">Website URL</div>
                                    <a
                                      href={event.website_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      data-testid="admin-event-website-link"
                                      className="text-crew-yellow hover:underline text-sm break-all inline-flex items-center gap-1"
                                    >
                                      {event.website_url}
                                    </a>
                                  </div>
                                )}
                                
                                {(event.description || event.notes) && (
                                  <div>
                                    <div className="text-xs text-zinc-500 uppercase font-bold mb-1">Notes / Description</div>
                                    <div className="bg-black/50 rounded border border-zinc-800 p-3 max-h-40 overflow-y-auto">
                                      <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-sans">
                                        {event.description || event.notes || '—'}
                                      </pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
  );
};
